import React, { useEffect, useState } from 'react';
import { IconPlus, IconFileText, IconCalendar, IconTrash, IconEdit } from '@tabler/icons-react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import AddItems from './add-items';
import FooterText from './footer-text';
import Select from 'react-select';
import { getRequest } from 'services/api';
import { useInvoiceCreate } from 'contexts/v1/pages/InvoiceCreateContext';
import CurrencyInput from 'components/form/currency-input';
import { ACTION_TYPES, DISCOUNT_TYPES, RECURRING_TYPES } from 'utils/constants';
import { formatPrice } from 'utils/utils';

// Custom Swal with react component support
const MySwal = withReactContent(Swal);

const InvoiceForm = () => {
    const { invoice, setInvoice } = useInvoiceCreate();

    const handleAddItems = () => {
        MySwal.fire({
            title: `Add Items`,
            html: <AddItems
                onSuccess={(e) => {
                    setInvoice((prev) => ({
                        ...prev,
                        form: {
                            ...prev.form,
                            items: [...prev.form.items, e]
                        }
                    }));
                    MySwal.close();
                }}
                onCancel={() => {
                    MySwal.close();
                }} />,
            showCancelButton: false,
            cancelButtonText: "Cancel",
            showConfirmButton: false, // hide default button
        });
    };

    const handleFooterText = () => {
        MySwal.fire({
            title: `Footer Text`,
            html: <FooterText
                initialData={invoice.form.footer}
                saveItems={(value) => {
                    setInvoice((prev) => ({
                        ...prev,
                        form: {
                            ...prev.form,
                            footer: value.footer
                        }
                    }));
                    MySwal.close();
                }}
                onCancel={() => {
                    MySwal.close();
                }} />,
            showCancelButton: false,
            cancelButtonText: "Cancel",
            showConfirmButton: false, // hide default button
        });
    };

    return (
        <div className="card rounded-3">
            <div className="card-body">
                <div className='mb-3'>
                    <label className="form-label">Invoice Name</label>
                    <input type="text" className="form-control" name='name' placeholder='Invoice name' value={invoice.form.name} onChange={(e) => setInvoice((prev) => (
                        { ...prev, form: { ...prev.form, name: e.target.value } }
                    ))} />
                </div>

                {/* Customer Selection */}
                <CustomerSelect />

                {/* Receipment */}
                <RecipientsSelect />

                {/* Items List */}
                <ItemsList />

                {/* Footer Text and Items List */}
                <div className="mb-3 d-flex">
                    <button className="btn btn-outline-primary me-2" onClick={handleFooterText}>
                        <IconFileText size={18} /> Footer Text
                    </button>
                    <button className="btn btn-outline-primary" onClick={handleAddItems}>
                        <IconPlus size={18} /> Items List
                    </button>
                </div>

                {/* Payment Gateway Toggle */}
                {/* <div className="mb-3 d-flex align-items-center">
                    <label className="form-label me-3">Payment Gateway</label>
                    <div className="form-check form-switch">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            name='enable_payment_gateway'
                            checked={invoice.form.enable_payment_gateway}
                            onChange={(e) => setInvoice((prev) => ({
                                ...prev,
                                form: {
                                    ...prev.form,
                                    enable_payment_gateway: e.target.checked
                                }
                            }))}
                        />
                    </div>
                </div> */}

                {/* Tax & Services */}
                <div className="mb-3">
                    <label className="form-label">Tax & Services</label>
                    <input type="number" className="form-control" value={invoice.form.tax} name='tax' onChange={(e) => setInvoice((prev) => (
                        { ...prev, form: { ...prev.form, tax: e.target.value } }
                    ))} />
                </div>

                {/* Discount Section */}
                <DiscountSelect />

                {/* Date & Repeat Settings */}
                <DueDateSettings />
            </div>
        </div>
    );
};

export default InvoiceForm;

const CustomerSelect = () => {
    const { invoice, setInvoice } = useInvoiceCreate();
    const [customers, setCustomers] = useState({
        data: [],
        loading: false,
        error: null
    });

    const fetchCustomers = async () => {
        setCustomers((prev) => ({ ...prev, loading: true }));
        try {
            const response = await getRequest('/customer?all=1');
            if (response.success) {
                const data = response.data.map((item) => ({
                    label: item.name,
                    value: item.id,
                    originalData: item
                }));
                setCustomers({ data, loading: false, error: null });
            }
        } catch (error) {
            setCustomers((prev) => ({ ...prev, loading: false, error: 'Failed to load data Customers' }));
        }
    };

    useEffect(() => {
        fetchCustomers();
    }, []);

    return (
        <div className="mb-3">
            <label className="form-label">Select Customer</label>
            <Select
                options={customers.data}
                placeholder="Select Customer"
                formatOptionLabel={({ label, originalData }) => (
                    <div className="d-flex flex-column">
                        <span>{label}</span>
                        <span className="text-muted">{originalData?.email}</span>
                    </div>
                )}
                isLoading={customers.loading}
                onChange={(selectedOption) => {
                    setInvoice((prev) => ({
                        ...prev,
                        form: {
                            ...prev.form,
                            customer: selectedOption.originalData,
                        }
                    }));
                }}
                value={
                    invoice.mode === ACTION_TYPES.EDIT ? {
                        label: invoice.form.customer?.name,
                        value: invoice.form.customer?.id,
                        originalData: invoice.form.customer
                    } : {
                        label: invoice.form.customer?.name,
                        value: invoice.form.customer?.id,
                        originalData: invoice.form.customer
                    }
                }
            />
        </div>
    );
};

const ItemsList = () => {
    const { invoice, setInvoice } = useInvoiceCreate();

    return (
        <div className="table-responsive mb-3">
            <table className="table table-bordered table-striped">
                <thead className="table-light">
                    <tr>
                        <th>No</th>
                        <th>Name</th>
                        <th>Price</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {!invoice.form.items.length ? (
                        <tr>
                            <td colSpan="4" className='text-center'>No items</td>
                        </tr>
                    ) : invoice.form.items.map((item, index) => (
                        <tr key={index}>
                            <td>{index + 1}</td>
                            <td>{item.name}</td>
                            <td>{formatPrice(item.price)}</td>
                            <td>
                                <button className='btn btn-danger btn-icon ms-2' onClick={() => {
                                    setInvoice((prev) => ({
                                        ...prev,
                                        form: {
                                            ...prev.form,
                                            items: prev.form.items.filter((_, i) => i !== index)
                                        }
                                    }));
                                }}>
                                    <IconTrash size={20} />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
};

const DiscountSelect = () => {
    const { invoice, setInvoice } = useInvoiceCreate();

    const determineIsCustom = (invoice) => {
        // Jika discount null/tidak ada
        if (!invoice.form.discount) {
            console.log('no discount');
            return true;
        }
        // Jika dalam mode edit dan discount tidak ada
        if (invoice.mode === ACTION_TYPES.EDIT && !invoice.form.discount) {
            console.log('no discount 2');
            return true;
        }
        // Jika dalam mode edit dan discount ada
        if (invoice.mode === ACTION_TYPES.EDIT && invoice.form.discount) {
            console.log('discount');
            return false;
        }
        // Default
        return false;
    };

    const [discounts, setDiscounts] = useState({
        data: [],
        loading: false,
        error: null,
        isCustom: determineIsCustom(invoice)
    });

    const fetchDiscounts = async () => {
        setDiscounts((prev) => ({ ...prev, loading: true }));
        try {
            const response = await getRequest('/discount');
            if (response.success) {
                const data = response.data.map((item) => ({
                    label: item.name,
                    value: item.id,
                    originalData: item
                }));
                setDiscounts((prev) => ({ ...prev, data: data, loading: false }));
            }
        } catch (error) {
            setDiscounts((prev) => ({ ...prev, loading: false, error: 'Failed to load data Discounts' }));
        }
    };

    useEffect(() => {
        fetchDiscounts();
    }, []);

    return (
        <>
            {discounts.isCustom && (!invoice.mode === ACTION_TYPES.EDIT || !invoice.form.discount) ? (
                <div className="mb-3">
                    <label className="form-label">Select Discount</label>
                    <Select
                        options={discounts.data}
                        placeholder="Select Discount"
                        isLoading={discounts.loading}
                        onChange={(selectedOption) => {
                            setInvoice((prev) => ({
                                ...prev,
                                form: {
                                    ...prev.form,
                                    discount: selectedOption.originalData.value,
                                    discount_type: selectedOption.originalData.type
                                }
                            }));
                        }}
                    />
                    {invoice.form.discount && !discounts.isCustom && (
                        <button
                            className="text-primary px-0 pt-2 border-0 bg-white shadow-none"
                            onClick={() =>
                                setDiscounts((prev) => ({ ...prev, isCustom: !prev.isCustom }))
                            }
                        >
                            <IconEdit size={14} /> Custom Discount
                        </button>
                    )}
                </div>
            ) : (
                <CustomDiscount setDiscounts={setDiscounts} />
            )}
        </>
    );
};

const CustomDiscount = ({ setDiscounts }) => {
    const { invoice, setInvoice } = useInvoiceCreate();

    return (
        <div>
            <div className='mb-3'>
                <label className="form-label">Discount Type</label>
                {/* only show type */}
                <Select
                    options={DISCOUNT_TYPES}
                    placeholder="Select Discount Type"
                    onChange={(selectedOption) => {
                        setInvoice((prev) => ({
                            ...prev,
                            form: {
                                ...prev.form,
                                discount_type: selectedOption.value
                            }
                        }));
                    }}
                    value={
                        DISCOUNT_TYPES.find((type) => type.value === invoice.form.discount_type)
                    }
                />
            </div>
            <div className="mb-3">
                <label className="form-label">Discount</label>
                {invoice.form.discount_type === DISCOUNT_TYPES[1].value ? (
                    <>
                        <div className="input-group">
                            <input type="number" className="form-control" defaultValue="0" name='discount' value={invoice.form.discount} onChange={(e) => setInvoice((prev) => (
                                { ...prev, form: { ...prev.form, discount: e.target.value } }
                            ))} />
                            <span className="input-group-text">%</span>
                        </div>
                    </>
                ) : (
                    <CurrencyInput
                        label="Discount"
                        name="discount"
                        value={invoice.form.discount}
                        onChange={(e) => setInvoice((prev) => ({
                            ...prev,
                            form: {
                                ...prev.form,
                                discount: e.target.value
                            }
                        }))}
                    />
                )}
                {/* button hide/remove input */}
                <button
                    className="text-danger px-0 pt-2 border-0 bg-white shadow-none"
                    onClick={() => {
                        setDiscounts((prev) => ({
                            ...prev,
                            isCustom: true, // Kembali ke mode Select Discount
                        }));
                        setInvoice((prev) => ({
                            ...prev,
                            form: {
                                ...prev.form,
                                discount: null, // Reset nilai discount
                                discount_type: null, // Reset tipe discount jika ada
                            },
                        }));
                    }}
                >
                    <IconTrash size={14} /> Remove Custom Discount
                </button>
            </div>
        </div>
    );
};

const DueDateSettings = () => {
    const { invoice, setInvoice } = useInvoiceCreate();

    const calculateReminderDate = (dueDate, reminder) => {
        const dueDateObj = new Date(dueDate); // Pastikan dueDate adalah objek Date
        const reminderInMilliseconds = reminder * 24 * 60 * 60 * 1000; // Konversi hari ke milidetik
        const reminderDate = new Date(dueDateObj.getTime() - reminderInMilliseconds); // Kurangi reminder dari due_date
        return reminderDate.toISOString().split("T")[0];
    };

    return (
        <div className="card my-4">
            <div className="card-body">
                <h4>Date & Repeat Settings</h4>

                {/* Due Date */}
                <div className="mb-3">
                    <label className="form-label">Due Date</label>
                    <div className="input-icon">
                        <input type="date" className="form-control" onChange={
                            (e) => setInvoice((prev) => ({
                                ...prev,
                                form: {
                                    ...prev.form,
                                    due_date: e.target.value
                                }
                            }))
                        } name='due_date' value={invoice.form.due_date} />
                        <span className="input-icon-addon">
                            <IconCalendar size={18} />
                        </span>
                    </div>
                </div>

                <div className="mb-3">
                    <label className="form-label">At</label>
                    <input type="time" className="form-control" defaultValue="10:00" name='reminder_time' onChange={
                        (e) => setInvoice((prev) => ({
                            ...prev,
                            form: {
                                ...prev.form,
                                reminder_time: e.target.value
                            }
                        }))
                    } value={invoice.form.reminder_time} />
                </div>

                {/* Repeat Toggle */}
                <div className="mb-3 d-flex align-items-center">
                    <label className="form-label me-3">Repeat?</label>
                    <div className="form-check form-switch">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            checked={invoice.form.is_recurring}
                            onChange={
                                (e) => setInvoice((prev) => ({
                                    ...prev,
                                    form: {
                                        ...prev.form,
                                        is_recurring: e.target.checked
                                    }
                                }))}
                            name='is_recurring'
                        />
                    </div>
                </div>

                {/* Reminder Settings */}
                {invoice.form.is_recurring && (
                    <>
                        <div className="mb-3">
                            <label className="form-label">Reminder</label>
                            <div className="d-flex">
                                <input type="number" className="form-control me-2" defaultValue="3" name="reminder" onChange={
                                    (e) => setInvoice((prev) => ({
                                        ...prev,
                                        form: {
                                            ...prev.form,
                                            reminder: e.target.value
                                        }
                                    }))
                                } value={invoice.form.reminder} />
                                <span className="form-label">Day/s Before</span>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Running at</label>
                            <p>{calculateReminderDate(invoice.form.due_date, invoice.form.reminder)} @ {invoice.form.reminder_time}</p>
                        </div>

                        {/* Repeat Setting */}
                        <div className="mb-3">
                            <label className="form-label">Repeat Setting</label>
                            <Select
                                options={[
                                    { label: 'Daily', value: 'daily' },
                                    { label: 'Monthly', value: 'monthly' },
                                    { label: 'Weekly', value: 'weekly' },
                                    { label: 'Annually', value: 'yearly' }
                                ]}
                                placeholder="Select Repeat Setting"
                                onChange={(selectedOption) => {
                                    setInvoice((prev) => ({
                                        ...prev,
                                        form: {
                                            ...prev.form,
                                            recurring_type: selectedOption
                                        }
                                    }));
                                }}
                                value={
                                    invoice.mode === ACTION_TYPES.EDIT ? RECURRING_TYPES.find((type) => type.value === invoice.form.recurring_type) : invoice.form.recurring_type
                                }
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const RecipientsSelect = () => {
    // take from customer api and for select will be multiple
    const { invoice, setInvoice } = useInvoiceCreate();
    const [recipients, setRecipients] = useState({
        data: [],
        loading: false,
        error: null
    });

    const fetchRecipients = async () => {
        setRecipients((prev) => ({ ...prev, loading: true }));
        try {
            const response = await getRequest('/customer?all=1');
            if (response.success) {
                const data = response.data.map((item) => ({
                    label: item.name,
                    value: item.id,
                    originalData: item
                }));
                setRecipients({ data, loading: false, error: null });
            }
        } catch (error) {
            setRecipients((prev) => ({ ...prev, loading: false, error: 'Failed to load data Recipients' }));
        }
    };

    useEffect(() => {
        fetchRecipients();
    }, []);

    return (
        <div className="mb-3">
            <label className="form-label">Select Recipients</label>
            <Select
                options={recipients.data}
                formatOptionLabel={({ label, originalData }) => (
                    <div className="d-flex flex-column">
                        <span>{label}</span>
                        <span className="text-muted">{originalData?.email} | {originalData?.whatsapp}</span>
                    </div>
                )}
                placeholder="Select Recipients"
                isMulti
                isLoading={recipients.loading}
                onChange={(selectedOption) => {
                    console.log(selectedOption);
                    setInvoice((prev) => ({
                        ...prev,
                        form: {
                            ...prev.form,
                            recipients: selectedOption.map((item) => item)
                        }
                    }));
                }}
                value={
                    invoice.mode === ACTION_TYPES.EDIT ? invoice.form.recipients.map((recipient) => ({
                        label: recipient.label,
                        value: recipient.value,
                        originalData: recipient.originalData
                    })) : invoice.form.recipients.map((recipient) => ({
                        label: recipient.label,
                        value: recipient.value,
                        originalData: recipient.originalData
                    }))
                }
            />
        </div>
    );
};